import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { useMemo } from 'react';
import IconButton from '../../../../../icon-button';
import LinkButton from '../../../../../link-button';
import TextInput from '../../../../../text-input';
import { type CompositeMode, findDuplicateNames, generateId, type SchemaProperty, type SchemaPropertyType } from '../../../../utils/schema-model';
import { INDENT_PER_DEPTH } from '../../constants';
import CompositeSchemaRow from '../composite-schema-row';
import EnumEditor from '../enum-editor';
import TypeSelector from '../type-selector';
type PropertyRowProps = {
  property: SchemaProperty;
  onChange: (property: SchemaProperty) => void;
  onDelete: () => void;
  depth: number;
  error?: string;
  isDisabled?: boolean;
};
const PropertyRow = t0 => {
  const $ = _c(98);
  const {
    property,
    onChange,
    onDelete,
    depth,
    error,
    isDisabled: t1
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== onChange || $[1] !== property) {
    t2 = name => {
      onChange({
        ...property,
        name
      });
    };
    $[0] = onChange;
    $[1] = property;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleNameChange = t2;
  let t3;
  if ($[3] !== onChange || $[4] !== property) {
    t3 = (type, arrayItemType, compositeMode) => {
      const updated = {
        ...property,
        type
      };
      if (type === "array") {
        updated.arrayItemType = arrayItemType || "string";
        if (arrayItemType === "object") {
          updated.arrayItemChildren = updated.arrayItemChildren || [];
        }
      } else {
        if (type === "object") {
          updated.children = updated.children || [];
        } else {
          if (type === "enum") {
            updated.enumValues = updated.enumValues || [];
          } else {
            if (type === "composite") {
              updated.compositeMode = compositeMode || "oneOf";
              updated.schemas = updated.schemas || [];
            }
          }
        }
      }
      onChange(updated);
    };
    $[3] = onChange;
    $[4] = property;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleTypeChange = t3;
  let t4;
  if ($[6] !== onChange || $[7] !== property) {
    t4 = description => {
      onChange({
        ...property,
        description: description || undefined
      });
    };
    $[6] = onChange;
    $[7] = property;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleDescriptionChange = t4;
  let t5;
  if ($[9] !== onChange || $[10] !== property) {
    t5 = (index, child) => {
      if (property.type === "object") {
        const newChildren = [...(property.children || [])];
        newChildren[index] = child;
        onChange({
          ...property,
          children: newChildren
        });
      } else {
        if (property.type === "array" && property.arrayItemType === "object") {
          const newChildren_0 = [...(property.arrayItemChildren || [])];
          newChildren_0[index] = child;
          onChange({
            ...property,
            arrayItemChildren: newChildren_0
          });
        }
      }
    };
    $[9] = onChange;
    $[10] = property;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleChildChange = t5;
  let t6;
  if ($[12] !== onChange || $[13] !== property) {
    t6 = index_0 => {
      if (property.type === "object") {
        const newChildren_1 = (property.children || []).filter((_, i) => i !== index_0);
        onChange({
          ...property,
          children: newChildren_1
        });
      } else {
        if (property.type === "array" && property.arrayItemType === "object") {
          const newChildren_2 = (property.arrayItemChildren || []).filter((__0, i_0) => i_0 !== index_0);
          onChange({
            ...property,
            arrayItemChildren: newChildren_2
          });
        }
      }
    };
    $[12] = onChange;
    $[13] = property;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleChildDelete = t6;
  let t7;
  if ($[15] !== onChange || $[16] !== property) {
    t7 = () => {
      const existingChildren = property.children || property.arrayItemChildren || [];
      const existingNames = new Set(existingChildren.map(_temp));
      let counter = existingChildren.length + 1;
      let newName = `field_${counter}`;
      while (existingNames.has(newName)) {
        counter++;
        newName = `field_${counter}`;
      }
      const newChild = {
        id: generateId(),
        name: newName,
        type: "string"
      };
      if (property.type === "object") {
        onChange({
          ...property,
          children: [...(property.children || []), newChild]
        });
      } else {
        if (property.type === "array" && property.arrayItemType === "object") {
          onChange({
            ...property,
            arrayItemChildren: [...(property.arrayItemChildren || []), newChild]
          });
        }
      }
    };
    $[15] = onChange;
    $[16] = property;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleAddChild = t7;
  let t8;
  if ($[18] !== onChange || $[19] !== property) {
    t8 = (index_1, schema) => {
      const newSchemas = [...(property.schemas || [])];
      newSchemas[index_1] = schema;
      onChange({
        ...property,
        schemas: newSchemas
      });
    };
    $[18] = onChange;
    $[19] = property;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleSchemaChange = t8;
  let t9;
  if ($[21] !== onChange || $[22] !== property) {
    t9 = index_2 => {
      const newSchemas_0 = (property.schemas || []).filter((__1, i_1) => i_1 !== index_2);
      onChange({
        ...property,
        schemas: newSchemas_0
      });
    };
    $[21] = onChange;
    $[22] = property;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleSchemaDelete = t9;
  let t10;
  if ($[24] !== onChange || $[25] !== property) {
    t10 = () => {
      const existingSchemas = property.schemas || [];
      const newSchema = {
        id: generateId(),
        name: `schema_${existingSchemas.length + 1}`,
        type: "string"
      };
      onChange({
        ...property,
        schemas: [...existingSchemas, newSchema]
      });
    };
    $[24] = onChange;
    $[25] = property;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const handleAddSchema = t10;
  let t11;
  if ($[27] !== onChange || $[28] !== property) {
    t11 = values => {
      onChange({
        ...property,
        enumValues: values
      });
    };
    $[27] = onChange;
    $[28] = property;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const handleEnumValuesChange = t11;
  const children = property.type === "object" ? property.children : property.arrayItemChildren;
  const isObjectType = property.type === "object";
  const isArrayOfObjects = property.type === "array" && property.arrayItemType === "object";
  const isComposite = property.type === "composite";
  const canHaveChildren = isObjectType || isArrayOfObjects;
  let t12;
  bb0: {
    if (!children) {
      let t13;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = new Set();
        $[30] = t13;
      } else {
        t13 = $[30];
      }
      t12 = t13;
      break bb0;
    }
    let t13;
    if ($[31] !== children) {
      t13 = findDuplicateNames(children);
      $[31] = children;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    t12 = t13;
  }
  const childDuplicates = t12;
  const indent = depth * INDENT_PER_DEPTH;
  const childIndent = (depth + 1) * INDENT_PER_DEPTH;
  const isTopLevel = depth === 0;
  const t13 = isTopLevel && "rounded-md border border-tertiary bg-primary p-3";
  let t14;
  if ($[33] !== t13) {
    t14 = cx("flex flex-col gap-2", t13);
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = isTopLevel ? 0 : indent;
  let t16;
  if ($[35] !== t15) {
    t16 = {
      paddingLeft: t15
    };
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = !!error;
  let t18;
  if ($[37] !== handleNameChange || $[38] !== isDisabled || $[39] !== property.name || $[40] !== t17) {
    t18 = <TextInput size="compact" value={property.name} onChangeText={handleNameChange} placeholder="Name *" disabled={isDisabled} hasError={t17} containerClassName="w-36 min-w-0 flex-shrink-0 [&_.fp-input-container]:mb-0" />;
    $[37] = handleNameChange;
    $[38] = isDisabled;
    $[39] = property.name;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = property.description || "";
  let t20;
  if ($[42] !== handleDescriptionChange || $[43] !== isDisabled || $[44] !== t19) {
    t20 = <TextInput size="compact" value={t19} onChangeText={handleDescriptionChange} placeholder="Description (optional)" disabled={isDisabled} containerClassName="min-w-0 flex-1 [&_.fp-input-container]:mb-0" />;
    $[42] = handleDescriptionChange;
    $[43] = isDisabled;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== handleTypeChange || $[47] !== isDisabled || $[48] !== property.arrayItemType || $[49] !== property.compositeMode || $[50] !== property.type) {
    t21 = <TypeSelector type={property.type} arrayItemType={property.arrayItemType} compositeMode={property.compositeMode} onChange={handleTypeChange} isDisabled={isDisabled} />;
    $[46] = handleTypeChange;
    $[47] = isDisabled;
    $[48] = property.arrayItemType;
    $[49] = property.compositeMode;
    $[50] = property.type;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== isDisabled || $[53] !== onDelete) {
    t22 = <IconButton aria-label="Delete property" onClick={onDelete} size="compact" icon={IcoTrash16} destructive={true} disabled={isDisabled} />;
    $[52] = isDisabled;
    $[53] = onDelete;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t21 || $[56] !== t22) {
    t23 = <div className="flex flex-shrink-0 items-stretch gap-1 self-stretch">{t21}{t22}</div>;
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t16 || $[59] !== t18 || $[60] !== t20 || $[61] !== t23) {
    t24 = <div className="flex items-center gap-3" style={t16}>{t18}{t20}{t23}</div>;
    $[58] = t16;
    $[59] = t18;
    $[60] = t20;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== error || $[64] !== indent) {
    t25 = error && <p className="text-caption-1 text-error" style={{
      paddingLeft: indent
    }}>{error}</p>;
    $[63] = error;
    $[64] = indent;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== handleEnumValuesChange || $[67] !== indent || $[68] !== isDisabled || $[69] !== property.enumValues || $[70] !== property.type) {
    t26 = property.type === "enum" && <div style={{
      paddingLeft: indent
    }}><EnumEditor values={property.enumValues || []} onChange={handleEnumValuesChange} isDisabled={isDisabled} /></div>;
    $[66] = handleEnumValuesChange;
    $[67] = indent;
    $[68] = isDisabled;
    $[69] = property.enumValues;
    $[70] = property.type;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== childIndent || $[73] !== depth || $[74] !== handleAddSchema || $[75] !== handleSchemaChange || $[76] !== handleSchemaDelete || $[77] !== isComposite || $[78] !== isDisabled || $[79] !== property.schemas) {
    t27 = isComposite && <div className="flex flex-col gap-2">{(property.schemas || []).map((schema_0, index_3) => <CompositeSchemaRow key={schema_0.id} schema={schema_0} onChange={prop => handleSchemaChange(index_3, prop)} onDelete={() => handleSchemaDelete(index_3)} depth={depth + 1} isDisabled={isDisabled} />)}<div style={{
        paddingLeft: childIndent
      }}><LinkButton onClick={handleAddSchema} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={isDisabled}>Add schema</LinkButton></div></div>;
    $[72] = childIndent;
    $[73] = depth;
    $[74] = handleAddSchema;
    $[75] = handleSchemaChange;
    $[76] = handleSchemaDelete;
    $[77] = isComposite;
    $[78] = isDisabled;
    $[79] = property.schemas;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== canHaveChildren || $[82] !== childDuplicates || $[83] !== childIndent || $[84] !== children || $[85] !== depth || $[86] !== handleAddChild || $[87] !== handleChildChange || $[88] !== handleChildDelete || $[89] !== isDisabled) {
    t28 = canHaveChildren && <div className="flex flex-col gap-2">{(children || []).map((child_0, index_4) => <PropertyRow key={child_0.id} property={child_0} onChange={prop_0 => handleChildChange(index_4, prop_0)} onDelete={() => handleChildDelete(index_4)} depth={depth + 1} error={childDuplicates.has(child_0.name) ? "Duplicate property name" : undefined} isDisabled={isDisabled} />)}<div style={{
        paddingLeft: childIndent
      }}><LinkButton onClick={handleAddChild} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={isDisabled}>Add property</LinkButton></div></div>;
    $[81] = canHaveChildren;
    $[82] = childDuplicates;
    $[83] = childIndent;
    $[84] = children;
    $[85] = depth;
    $[86] = handleAddChild;
    $[87] = handleChildChange;
    $[88] = handleChildDelete;
    $[89] = isDisabled;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== t14 || $[92] !== t24 || $[93] !== t25 || $[94] !== t26 || $[95] !== t27 || $[96] !== t28) {
    t29 = <div className={t14}>{t24}{t25}{t26}{t27}{t28}</div>;
    $[91] = t14;
    $[92] = t24;
    $[93] = t25;
    $[94] = t26;
    $[95] = t27;
    $[96] = t28;
    $[97] = t29;
  } else {
    t29 = $[97];
  }
  return t29;
};
export default PropertyRow;
function _temp(c) {
  return c.name;
}